import { computed, ref } from 'vue'
import { consola } from 'consola'
import { useModalStore, modalStateEnum } from '@shared/model'
import { useInstitutionStore, hasFreshBranchChoice } from '@entities/institution'
import { isAddressPromptOpen } from '~/features/delivery-address-prompt/@x/branch-select-prompt'

const logger = consola.withTag('branch-select-prompt')

/** Пауза после запуска: даём приложению отрисоваться (интро-видео шторку перекрывает само). */
const PROMPT_DELAY_MS = 2500
/** Повторная попытка, когда в нужный момент открыта модалка или филиалы ещё грузятся. */
const RETRY_DELAY_MS = 3000

// ── Состояние модуля (singleton на одну загрузку страницы) ──
// isOpen — открыта ли нижняя шторка. dismissedThisLoad — пользователь нажал «Не сейчас»:
// в эту загрузку больше не дёргаем. Флаг намеренно НЕ персистентный — по требованию
// блок показывается при каждом новом запуске приложения, пока нет сохранённого выбора.
const isOpen = ref(false)
let dismissedThisLoad = false
let timer: ReturnType<typeof setTimeout> | null = null
let started = false

/** Открыта ли шторка выбора филиала — для координации с другими стартовыми промптами. */
export const isBranchPromptOpen = computed(() => isOpen.value)

function clearTimer(): void {
  if (timer != null) {
    clearTimeout(timer)
    timer = null
  }
}

/**
 * Стартовая шторка «Выберите филиал».
 *
 * Показывается при каждом запуске приложения, пока у пользователя нет СВЕЖЕГО
 * (моложе суток) явного выбора филиала (entities/institution/lib/branchChoice):
 *  - выбор сделан («Выбрать локацию» → карта, либо селект в шапке/футере) →
 *    сохраняется на сутки и переживает обновление страницы — шторка молчит;
 *  - «Не сейчас» → прячем до следующего запуска приложения;
 *  - через сутки выбор протухает — шторка спросит локацию заново.
 *
 * Гостям показываем наравне с авторизованными: филиал (меню, цены, зоны) нужен до логина.
 */
export function useBranchSelectPrompt() {
  const modalStore = useModalStore()

  /** Показать шторку, если условия всё ещё выполняются. */
  function fire(): void {
    timer = null
    if (!import.meta.client) return
    if (dismissedThisLoad) return
    // Свежий выбор уже есть — не нудим (главный сценарий после F5).
    if (hasFreshBranchChoice()) return

    const institutionStore = useInstitutionStore()
    const branchCount = institutionStore.branchOptions?.length ?? 0

    // Филиалы ещё не загрузились — попробуем позже.
    if (branchCount === 0) {
      timer = setTimeout(fire, RETRY_DELAY_MS)
      return
    }
    // Один филиал — выбирать нечего, блок не показываем вовсе.
    if (branchCount === 1) return

    // Поверх другой модалки (логин/онбординг/карта) или адресной шторки не
    // показываем — подождём: адресная в modalStore не живёт, а при долгой
    // загрузке филиалов (>30с ретраев) обе стартовые шторки открывались разом.
    if (modalStore.isOpen || isAddressPromptOpen.value) {
      timer = setTimeout(fire, RETRY_DELAY_MS)
      return
    }

    isOpen.value = true
    logger.info('Показываем стартовую шторку выбора филиала')
  }

  /**
   * Точка входа: вызывается из layout в setup. Планирует показ после короткой паузы.
   * Авторизация не нужна — филиал выбирают и гости.
   */
  function start(): void {
    if (!import.meta.client) return
    if (started) return
    started = true
    clearTimer()
    timer = setTimeout(fire, PROMPT_DELAY_MS)
  }

  /**
   * «Выбрать локацию» — закрываем шторку и открываем карту с филиалами
   * (таб «С собой»; на доставку пользователь переключится табом там же).
   * Выбор филиала/адреса внутри карты сохраняется через chooseBranch на сутки.
   */
  function chooseLocation(): void {
    isOpen.value = false
    clearTimer()
    modalStore.open(modalStateEnum.ModalMapYandex, { orderType: 'takeaway' })
  }

  /** «Не сейчас» — прячем шторку до следующего запуска приложения. */
  function dismiss(): void {
    isOpen.value = false
    dismissedThisLoad = true
    clearTimer()
  }

  return { isOpen, start, chooseLocation, dismiss }
}
